import * as PortOne from "@portone/browser-sdk/v2";
import { useState } from "react";

function generateUUID(): string {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

interface IdentityVerificationResult {
  identityVerificationId: string;
}

interface UseIdentityVerificationReturn {
  requestVerification: () => Promise<IdentityVerificationResult>;
  isLoading: boolean;
  error: string | null;
}

export function useIdentityVerification(): UseIdentityVerificationReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const requestVerification = async (): Promise<IdentityVerificationResult> => {
    setIsLoading(true);
    setError(null);

    const identityVerificationId = `identity-verification-${generateUUID()}`;

    if (import.meta.env.VITE_PORTONE_MOCK === "true") {
      setIsLoading(false);
      return { identityVerificationId: `mock-${generateUUID()}` };
    }

    try {
      const response = await PortOne.requestIdentityVerification({
        storeId: import.meta.env.VITE_PORTONE_STORE_ID,
        channelKey: import.meta.env.VITE_PORTONE_IDENTITY_CHANNEL_KEY,
        identityVerificationId,
        redirectUrl: `${window.location.origin}${window.location.pathname}`,
        bypass: {
          inicisUnified: {
            flgFixedUser: "N",
          },
        },
      });

      if (response === null) {
        throw new Error("CANCELLED");
      }

      if (response?.code !== undefined) {
        const message = response.message ?? "본인인증에 실패했습니다.";
        setError(message);
        throw new Error(message);
      }

      return { identityVerificationId };
    } finally {
      setIsLoading(false);
    }
  };

  return { requestVerification, isLoading, error };
}
